import {
  MAX_UPLOAD_CONTENT_HASH_SIZE_BYTES,
  UPLOADED_MEETINGS_QUERY_KEY,
} from "@frontend-shared/constants";
import {
  EAnalyticsEvent,
  getFileSizeBucket,
} from "@frontend-shared/product-analytics";
import {
  EFileUploadStatus,
  type IFileUploadEntry,
  type IUploadAudioFileParams,
  type IUploadIdentity,
} from "@frontend-shared/types";
import {
  classifyUploadFailure,
  computeBlobContentHash,
  createAbortError,
  isSameUploadInFlight,
  uploadFileToS3,
} from "@frontend-shared/utils";
import { useQueryClient } from "@tanstack/react-query";
import { isCancel } from "axios";
import type { PropsWithChildren, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { vstrAudioApi } from "@/api/services";
import { resolveUploadFailureReason } from "@/api/utils";
import { MeetingUploadContext } from "@/contexts/MeetingUploadContext";
import { useAuth } from "@/hooks/use-auth";
import { useProductAnalytics } from "@/hooks/use-product-analytics";

// NOTE: Owns every meeting upload so the transfer and its progress live above
// the routes instead of inside the Uploads page. Before NSAI-5141 the state sat
// in the page, which forced a navigation guard: a user who dropped several
// files and then clicked an already-processed recording was told "Audio/video
// upload in progress … leaving will cancel the upload" and could not open it.
// With the state up here, in-app navigation is not guarded at all — the upload
// keeps running, the sidebar shows how many are still in flight, and only a
// refresh / tab-close (which really does kill the XHR) gets a beforeunload
// prompt. Mirrors what MediaDownloadProvider does for downloads (NSAI-3838).
const MeetingUploadProvider = ({ children }: PropsWithChildren): ReactNode => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("uploads");
  const { user, currentProjectUuid } = useAuth();
  const { capture } = useProductAnalytics();

  const [uploads, setUploads] = useState<IFileUploadEntry[]>([]);
  // NOTE: One AbortController per in-flight upload so a row's X can stop just
  // that S3 PUT, and a logout can stop all of them.
  const abortControllersRef = useRef(new Map<string, AbortController>());
  // NOTE: Uploads aborted by the session-scope purge (logout / account or
  // project switch) rather than by the user. Their AbortError must not surface
  // a "cancelled" toast — the user did not cancel anything.
  const silentAbortsRef = useRef(new Set<string>());
  // NOTE: NSAI-5560 — which file each still-running upload is carrying, so a
  // second pick of the same file can be refused before it starts. A ref rather
  // than `uploads`: the answer has to be right at the instant of the pick, and
  // a state read inside this callback is one render behind — two picks in quick
  // succession would both see an empty list and both start. Entries live from
  // the moment the transfer begins until it settles, which is exactly the
  // window in which a duplicate is wasted work.
  const inFlightUploadsRef = useRef(new Map<string, IUploadIdentity>());

  const updateUpload = useCallback(
    (uploadId: string, update: Partial<IFileUploadEntry>): void => {
      setUploads((previous) =>
        previous.map((entry) =>
          entry.id === uploadId ? { ...entry, ...update } : entry,
        ),
      );
    },
    [],
  );

  const removeUpload = useCallback((uploadId: string): void => {
    setUploads((previous) => previous.filter((entry) => entry.id !== uploadId));
  }, []);

  const uploadFile = useCallback(
    ({ file, fileName }: IUploadAudioFileParams): boolean => {
      const uploadId = crypto.randomUUID();
      const name = fileName ?? file.name;
      const identity: IUploadIdentity = {
        fileName: name,
        fileSizeBytes: file.size,
      };

      // NOTE: NSAI-5560 — the same file, picked again while its first upload is
      // still running, used to start a second transfer of the same bytes. Both
      // reached the server, which refused the loser on the content hash — after
      // it had uploaded in full — and the user was left looking at two rows for
      // one recording. Refused here instead, before a byte moves. A file whose
      // upload has finished (or failed) is NOT held back: re-uploading then is
      // a legitimate retry, and the server decides whether the content is a
      // duplicate.
      if (
        isSameUploadInFlight({
          inFlightUploads: inFlightUploadsRef.current.values(),
          candidate: identity,
        })
      ) {
        toast.info(t("toasts.already_uploading"));
        return false;
      }

      const abortController = new AbortController();
      abortControllersRef.current.set(uploadId, abortController);
      inFlightUploadsRef.current.set(uploadId, identity);

      setUploads((previous) => [
        ...previous,
        {
          id: uploadId,
          fileName: name,
          progress: 0,
          status: EFileUploadStatus.UPLOADING,
        },
      ]);

      // NOTE: NSAI-4618 — started/completed as a pair, because the drop
      // between them IS the metric: a large recording that never finishes
      // uploading is the failure mode users abandon over, and only the pair
      // makes it visible. File name is never sent, only its size bucket.
      capture({
        event: EAnalyticsEvent.MEETING_UPLOAD_STARTED,
        properties: {
          file_size_bucket: getFileSizeBucket({ sizeBytes: file.size }),
        },
      });

      const run = async (): Promise<void> => {
        try {
          // NOTE: NSAI-5143 — hash the file BEFORE presigning so the server can
          // refuse duplicate content while the progress bar is still at 0%,
          // instead of at the end of a transfer that was always going to be
          // thrown away. `null` when the file is past
          // MAX_UPLOAD_CONTENT_HASH_SIZE_BYTES or `crypto.subtle` is unavailable
          // — the server's confirmUpload guard still catches those.
          const contentHash = await computeBlobContentHash({
            blob: file,
            maxSizeBytes: MAX_UPLOAD_CONTENT_HASH_SIZE_BYTES,
          });

          // NOTE: Hashing a large file takes long enough for the user to hit the
          // row's X in the meantime. Without this the abort is only noticed by
          // the S3 PUT, so a cancelled upload would still presign — and, for a
          // duplicate, still surface the rejection on a row the user dismissed.
          if (abortController.signal.aborted) {
            throw createAbortError("Upload cancelled");
          }

          // NOTE: Step 1 — presigned URL.
          const { uploadUrl, s3Key } = await vstrAudioApi.presignUpload({
            contentType: file.type,
            fileSizeBytes: file.size,
            ...(contentHash !== null && { contentHash }),
          });

          // NOTE: Step 2 — upload straight to S3 with progress.
          await uploadFileToS3({
            uploadUrl,
            body: file,
            contentType: file.type,
            onProgress: (percent) =>
              updateUpload(uploadId, { progress: percent }),
            signal: abortController.signal,
          });

          // NOTE: Step 3 — confirm, which is what creates the meeting row.
          updateUpload(uploadId, {
            status: EFileUploadStatus.CONFIRMING,
            progress: 100,
          });
          await vstrAudioApi.confirmUpload({
            s3Key,
            fileName: name,
            signal: abortController.signal,
          });

          // NOTE: Keep the row visible with a DONE state instead of removing
          // it + showing a toast. Mirrors the error-row pattern: persistent
          // inline feedback with an explicit dismiss button, so batch
          // uploads don't lose per-file context to stacked toasts.
          updateUpload(uploadId, { status: EFileUploadStatus.DONE });
          capture({ event: EAnalyticsEvent.MEETING_UPLOAD_COMPLETED });
          queryClient.invalidateQueries({
            queryKey: [UPLOADED_MEETINGS_QUERY_KEY],
          });
        } catch (error) {
          if (
            (error instanceof DOMException && error.name === "AbortError") ||
            isCancel(error)
          ) {
            removeUpload(uploadId);
            if (silentAbortsRef.current.has(uploadId)) {
              silentAbortsRef.current.delete(uploadId);
            } else {
              toast.info(t("toasts.upload_cancelled"));
            }
          } else {
            // NOTE: Surface the localized reason inline next to the failed row
            // so it persists for batch uploads where 1 of N fails. No toast —
            // when several files fail at once, stacked toasts hide each other
            // and the user can't tell which file each one is for.
            //
            // NOTE: NSAI-6451 — read through `resolveUploadFailureReason`, not
            // `extractServerErrorMessage`. The presign and confirm calls fail as
            // axios errors, but the S3 PUT between them throws NSAI-4883's
            // tagged error, which is a plain `Error`: the axios-only reader
            // returned null for every one of them, so a stalled transfer, a
            // dropped connection and a storage rejection all reached the user as
            // the single sentence "Failed to upload file". That is what made the
            // 1VM upload failures undiagnosable from a screenshot. This reader
            // falls back to the server message, so an axios rejection still
            // reads exactly as it did.
            const errorMessage =
              resolveUploadFailureReason(error) ?? t("toasts.upload_failed");
            updateUpload(uploadId, {
              status: EFileUploadStatus.ERROR,
              errorMessage,
            });

            // NOTE: NSAI-6451 — the started/completed pair (NSAI-4618) measures
            // the drop but cannot say why, so a rising failure rate could only
            // be investigated by asking a user for a screenshot. The reason is
            // the low-cardinality tag the uploader already assigns; the file
            // name never travels, only its size bucket, as on `started`.
            capture({
              event: EAnalyticsEvent.MEETING_UPLOAD_FAILED,
              properties: {
                reason: classifyUploadFailure({ error }).reason,
                file_size_bucket: getFileSizeBucket({ sizeBytes: file.size }),
              },
            });
          }
        } finally {
          abortControllersRef.current.delete(uploadId);
          // NOTE: NSAI-5560 — every terminal path passes through here, so the
          // file stops being "in flight" whether it was confirmed, refused or
          // cancelled, and can be picked again.
          inFlightUploadsRef.current.delete(uploadId);
        }
      };

      // NOTE: we need void to silence the eslint rule because we are using an async function
      void run(); // NOSONAR

      return true;
    },
    [capture, queryClient, removeUpload, t, updateUpload],
  );

  const cancelUpload = useCallback((uploadId: string): void => {
    abortControllersRef.current.get(uploadId)?.abort();
  }, []);

  // NOTE: Not exposed on the context — since NSAI-5141 nothing in the UI wants
  // to abort a whole batch; leaving /uploads is not a cancellation any more.
  // Only the session-scope purge below aborts in bulk, and it does so silently.
  const abortEveryUploadSilently = useCallback((): void => {
    for (const [uploadId, controller] of abortControllersRef.current) {
      silentAbortsRef.current.add(uploadId);
      controller.abort();
    }
    abortControllersRef.current.clear();
    inFlightUploadsRef.current.clear();
  }, []);

  const hasActiveUploads = uploads.some(
    (entry) =>
      entry.status === EFileUploadStatus.UPLOADING ||
      entry.status === EFileUploadStatus.CONFIRMING,
  );

  // NOTE: The S3 PUT is an XHR owned by this document, so a refresh or
  // tab-close really does kill it — warn before that happens. In-app
  // navigation is deliberately NOT guarded: the provider stays mounted and the
  // upload keeps running while the user reads another page (NSAI-5141).
  useEffect(() => {
    if (!hasActiveUploads) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      // NOTE: preventDefault() triggers the native "Leave site?" prompt in
      // modern browsers; older Chrome/Edge also needed returnValue.
      // Object.assign sets the (deprecated) property without a type assertion.
      event.preventDefault();
      Object.assign(event, { returnValue: "" });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasActiveUploads]);

  // NOTE: An upload belongs to the (user, project) it was started in, and it is
  // pinned to neither: `presignUpload` / `confirmUpload` pick up whatever
  // `X-Project-Uuid` the api client resolves AT REQUEST TIME (frontend-shared/
  // api/client.ts). So a switch mid-transfer would confirm the file into the
  // project the user landed on — a 403 there, or worse, a meeting created in
  // the wrong project. Rows also carry file names, which must not outlive the
  // session that produced them.
  //
  // Both cases are handled the same way: abort and drop everything. That is
  // what the rest of the app already does on a project switch — the
  // `removeQueries` calls in AuthProvider.onCurrentProjectChange cancel
  // in-flight requests for exactly this reason — and it is the purge
  // MediaDownloadAuthSync performs for downloads on identity change.
  //
  // Null until both halves are known, so the initial resolve (null -> scope)
  // does not read as a switch.
  const sessionScope =
    user?.uuid && currentProjectUuid
      ? `${user.uuid}:${currentProjectUuid}`
      : null;
  const previousScopeRef = useRef<string | null>(sessionScope);

  useEffect(() => {
    if (
      previousScopeRef.current !== null &&
      previousScopeRef.current !== sessionScope
    ) {
      abortEveryUploadSilently();
      setUploads([]);
    }
    previousScopeRef.current = sessionScope;
  }, [sessionScope, abortEveryUploadSilently]);

  const value = useMemo(
    () => ({
      uploads,
      uploadFile,
      cancelUpload,
      removeUpload,
    }),
    [uploads, uploadFile, cancelUpload, removeUpload],
  );

  return (
    <MeetingUploadContext.Provider value={value}>
      {children}
    </MeetingUploadContext.Provider>
  );
};

export default MeetingUploadProvider;
